import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { couponApi, orderApi, pointApi, purchaseRequestApi } from '../api';
import { toImageUrl } from '../utils/image';
import { Order, OrderItem, UserCoupon, PurchaseRequest } from '../types';
import ReviewWriteModal from '../components/review/ReviewWriteModal';
import ExtraChargePayment from '../components/purchaserequest/ExtraChargePayment';

type Tab = 'orders' | 'requests' | 'coupons';

const PR_STATUS: Record<string, string> = {
  REQUESTED: '신청됨', QUOTED: '견적됨', PAID: '결제완료',
  APPROVED: '승인', REJECTED: '거절', CANCELLED: '취소', SHIPPED: '발송됨',
};

const MyPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [coupons, setCoupons] = useState<UserCoupon[]>([]);
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [reviewTarget, setReviewTarget] = useState<{ productId: number; orderItemId: number; name: string; thumb: string } | null>(null);
  const [pointsUsd, setPointsUsd] = useState<number>(0);

  const loadRequests = () => purchaseRequestApi.getMine()
    .then(r => setRequests(r.data.data.content)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    orderApi.getMyOrders(page).then(r => {
      setOrders(r.data.data.content);
      setTotalPages(r.data.data.totalPages);
    });
    couponApi.getMyCoupons().then(r => setCoupons(r.data.data));
    pointApi.getMyPoints().then(r => setPointsUsd(r.data.data.balanceUsd)).catch(() => {});
    loadRequests();
  }, [page]);

  const openReview = (order: Order, item: OrderItem) => {
    setReviewTarget({
      productId: item.productId,
      orderItemId: item.orderItemId,
      name: item.name,
      thumb: toImageUrl(item.thumbnailUrl),
    });
  };

  const handleLogout = () => { logout(); navigate('/'); };

  if (!user) return null;

  const availableCoupons = coupons.filter(c => !c.used && !isExpired(c));
  const inactiveCoupons = coupons.filter(c => c.used || isExpired(c));
  const pendingExtra = requests.filter(r => (r.extraChargeUsd ?? 0) > 0 && !r.extraChargePaid).length;

  return (
    <div style={styles.page}>
      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          <div style={styles.profile}>
            <div style={styles.avatar}>{user.name[0]}</div>
            <p style={styles.profileName}>{user.name}</p>
            <p style={styles.profileEmail}>{user.email}</p>
            <div style={styles.pointBadge}>
              <span style={styles.pointLabel}>Points</span>
              <span style={styles.pointValue}>${Number(pointsUsd ?? 0).toFixed(2)}</span>
            </div>
          </div>
          <nav style={styles.nav}>
            <button
              style={{ ...styles.navItem, ...(tab === 'orders' ? styles.navItemActive : {}) }}
              onClick={() => setTab('orders')}
            >
              {t('mypage.title')}
            </button>
            <button
              style={{ ...styles.navItem, ...(tab === 'coupons' ? styles.navItemActive : {}) }}
              onClick={() => setTab('coupons')}
            >
              My Coupons
              {availableCoupons.length > 0 && (
                <span style={styles.couponBadge}>{availableCoupons.length}</span>
              )}
            </button>
            <button
              style={{ ...styles.navItem, ...(tab === 'requests' ? styles.navItemActive : {}) }}
              onClick={() => setTab('requests')}
            >
              대리구매
              {pendingExtra > 0 && <span style={styles.couponBadge}>{pendingExtra}</span>}
            </button>
            <button onClick={handleLogout} style={styles.logoutBtn}>{t('header.logout')}</button>
          </nav>
        </aside>

        <main style={styles.main}>
          {tab === 'orders' ? (
            <>
              <h2 style={styles.title}>{t('mypage.title')}</h2>
              {orders.length === 0 ? (
                <div style={styles.empty}>
                  <p>{t('mypage.noOrders')}</p>
                  <Link to="/products" style={styles.shopBtn}>{t('mypage.goShopping')}</Link>
                </div>
              ) : (
                orders.map(order => (
                  <div key={order.id} style={styles.orderCard}>
                    <div style={styles.orderHeader}>
                      <span style={styles.orderDate}>{order.createdAt.slice(0, 10)}</span>
                      <span style={styles.orderId}>{t('mypage.orderNumber')}#{order.orderNumber}</span>
                      <span style={styles.orderStatus}>{t(`orderStatus.${order.status}`)}</span>
                    </div>
                    {order.items.map(item => (
                      <div key={item.orderItemId} style={styles.orderItem}>
                        <img src={toImageUrl(item.thumbnailUrl)} alt="" style={styles.orderItemImg} />
                        <div style={{ flex: 1 }}>
                          <p style={styles.orderItemName}>{item.name}</p>
                          <p style={styles.orderItemQty}>{item.quantity}개</p>
                        </div>
                        <p style={styles.orderItemPrice}>${item.lineTotal.toFixed(2)}</p>
                        {order.status === 'DELIVERED' && (
                          <button
                            type="button"
                            style={styles.reviewBtn}
                            onClick={() => openReview(order, item)}
                          >Write Review</button>
                        )}
                      </div>
                    ))}
                    <div style={styles.orderFooter}>
                      <span style={styles.orderTotal}>{t('mypage.orderTotal')} ${order.totalAmount.toFixed(2)}</span>
                      <Link to={`/orders/${order.id}`} style={styles.detailBtn}>{t('mypage.viewDetail')}</Link>
                    </div>
                  </div>
                ))
              )}
            </>
          ) : tab === 'requests' ? (
            <>
              <h2 style={styles.title}>대리구매</h2>
              {requests.length === 0 ? (
                <div style={styles.empty}>
                  <p>대리구매 신청 내역이 없습니다.</p>
                  <Link to="/purchase-requests" style={styles.shopBtn}>신청하러 가기</Link>
                </div>
              ) : (
                requests.map(r => (
                  <div key={r.id} style={styles.orderCard}>
                    <div style={styles.orderHeader}>
                      <span style={styles.orderDate}>{r.createdAt.slice(0, 10)}</span>
                      <span style={styles.orderId}>{r.productName}</span>
                      <span style={styles.orderStatus}>{PR_STATUS[r.status] ?? r.status}</span>
                    </div>
                    {r.quotedPriceUsd != null && (
                      <p style={{ fontSize: 13, color: '#555' }}>견적: ${Number(r.quotedPriceUsd).toFixed(2)}</p>
                    )}
                    <ExtraChargePayment request={r} onPaid={loadRequests} />
                    {r.trackingNumber && (
                      <div style={{ marginTop: 10, fontSize: 13, color: '#6a1b9a' }}>
                        🚚 {r.trackingNumber}
                        {r.packingVideoUrl && <> · <a href={r.packingVideoUrl} target="_blank" rel="noreferrer" style={{ color: '#6a1b9a', fontWeight: 600 }}>📦 포장 영상 ↗</a></>}
                      </div>
                    )}
                    <div style={styles.orderFooter}>
                      <Link to="/purchase-requests" style={styles.detailBtn}>대리구매 페이지</Link>
                    </div>
                  </div>
                ))
              )}
            </>
          ) : (
            <>
              <h2 style={styles.title}>My Coupons</h2>
              {coupons.length === 0 ? (
                <div style={styles.empty}><p>No coupons available.</p></div>
              ) : (
                <>
                  {availableCoupons.length > 0 && (
                    <div style={styles.couponSection}>
                      <h3 style={styles.couponSectionTitle}>Available ({availableCoupons.length})</h3>
                      {availableCoupons.map(c => <CouponCard key={c.id} coupon={c} />)}
                    </div>
                  )}
                  {inactiveCoupons.length > 0 && (
                    <div style={styles.couponSection}>
                      <h3 style={{ ...styles.couponSectionTitle, color: '#bbb' }}>Used / Expired ({inactiveCoupons.length})</h3>
                      {inactiveCoupons.map(c => <CouponCard key={c.id} coupon={c} />)}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      </div>

      {reviewTarget && (
        <ReviewWriteModal
          productId={reviewTarget.productId}
          orderItemId={reviewTarget.orderItemId}
          productName={reviewTarget.name}
          thumbnailUrl={reviewTarget.thumb}
          onClose={() => setReviewTarget(null)}
          onSuccess={() => setReviewTarget(null)}
        />
      )}
    </div>
  );
};

const isExpired = (c: UserCoupon) => !!c.expiresAt && new Date(c.expiresAt) < new Date();

const CouponCard: React.FC<{ coupon: UserCoupon }> = ({ coupon }) => {
  const discount = coupon.discountType === 'FIXED'
    ? `$${coupon.discountValue} OFF`
    : `${coupon.discountValue}% OFF`;
  const expired = isExpired(coupon);
  const inactive = coupon.used || expired;

  return (
    <div style={{ ...cardStyles.card, opacity: inactive ? 0.5 : 1 }}>
      <div style={cardStyles.left}>
        <p style={cardStyles.discount}>{discount}</p>
        <p style={cardStyles.name}>{coupon.name}</p>
        {coupon.discountType === 'PERCENTAGE' && coupon.maxDiscountAmount && (
          <p style={cardStyles.condition}>Up to ${coupon.maxDiscountAmount} off</p>
        )}
        {coupon.minOrderAmount && (
          <p style={cardStyles.condition}>Min. order ${coupon.minOrderAmount}</p>
        )}
        {coupon.expiresAt && (
          <p style={cardStyles.condition}>Expires {coupon.expiresAt.slice(0, 10)}</p>
        )}
      </div>
      <div style={cardStyles.right}>
        {coupon.used
          ? <span style={cardStyles.usedTag}>USED</span>
          : expired
            ? <span style={cardStyles.expiredTag}>EXPIRED</span>
            : <span style={cardStyles.availableTag}>AVAILABLE</span>}
      </div>
    </div>
  );
};

const cardStyles: Record<string, React.CSSProperties> = {
  card: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1.5px dashed #e8c8c8', borderRadius: 12, padding: '16px 20px', marginBottom: 12 },
  left: { flex: 1 },
  discount: { fontSize: 22, fontWeight: 800, color: '#b39ddb', marginBottom: 4 },
  name: { fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 4 },
  condition: { fontSize: 12, color: '#999' },
  right: { flexShrink: 0, marginLeft: 16 },
  availableTag: { background: '#b39ddb', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 },
  usedTag: { background: '#e0e0e0', color: '#999', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 },
  expiredTag: { background: '#f3d6d6', color: '#c0392b', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 },
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '40px auto', padding: '0 20px' },
  layout: { display: 'flex', gap: 32 },
  sidebar: { width: 220, flexShrink: 0 },
  profile: { background: '#ece3f7', borderRadius: 12, padding: 24, textAlign: 'center', marginBottom: 16 },
  avatar: { width: 56, height: 56, borderRadius: '50%', background: '#b39ddb', color: '#fff', fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
  profileName: { fontWeight: 700, fontSize: 16 },
  profileEmail: { fontSize: 12, color: '#888', marginTop: 4 },
  pointBadge: { marginTop: 14, padding: '10px 12px', background: '#fff', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  pointLabel: { fontSize: 12, color: '#999' },
  pointValue: { fontSize: 15, fontWeight: 700, color: '#b39ddb' },
  nav: { display: 'flex', flexDirection: 'column', gap: 4 },
  navItem: { padding: '10px 16px', borderRadius: 8, fontSize: 14, color: '#888', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 },
  navItemActive: { color: '#b39ddb', fontWeight: 600, background: '#f8f5fd' },
  logoutBtn: { padding: '10px 16px', borderRadius: 8, fontSize: 14, color: '#888', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' },
  couponBadge: { background: '#b39ddb', color: '#fff', borderRadius: 10, fontSize: 11, padding: '2px 7px', fontWeight: 700 },
  main: { flex: 1 },
  title: { fontSize: 20, fontWeight: 700, marginBottom: 24 },
  empty: { textAlign: 'center', padding: '60px 0', color: '#bbb' },
  shopBtn: { display: 'inline-block', marginTop: 16, padding: '10px 24px', background: '#b39ddb', color: '#fff', borderRadius: 8, fontSize: 13 },
  orderCard: { background: '#fff', border: '1px solid #f0f0f0', borderRadius: 12, padding: 20, marginBottom: 16 },
  orderHeader: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f5f5f5' },
  orderDate: { fontSize: 13, color: '#888' },
  orderId: { fontSize: 13, color: '#555', flex: 1 },
  orderStatus: { fontSize: 12, padding: '4px 10px', background: '#ece3f7', color: '#b39ddb', borderRadius: 12, fontWeight: 600 },
  orderItem: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  orderItemImg: { width: 56, height: 56, objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' },
  orderItemName: { fontSize: 13, fontWeight: 500 },
  orderItemQty: { fontSize: 12, color: '#888', marginTop: 2 },
  orderItemPrice: { fontSize: 14, fontWeight: 600 },
  reviewBtn: { marginLeft: 12, padding: '6px 12px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  orderFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #f5f5f5' },
  orderTotal: { fontSize: 16, fontWeight: 700, color: '#b39ddb' },
  detailBtn: { padding: '8px 16px', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 13, color: '#555' },
  couponSection: { marginBottom: 32 },
  couponSectionTitle: { fontSize: 14, fontWeight: 600, color: '#666', marginBottom: 12 },
};

export default MyPage;
